/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { instantiateCombinedClient } from './features/azure-b2c/NativeNavigationClient'
import { useAppDispatch } from './store/rootStore'
import useMsalFunctions from './util/useMsalAuthFunctions'
import AuthSlice, {
  AuthAccount,
  getAccounts,
  selectAllFarmerAccounts,
} from './features/azure-b2c/storage'
import apiClient from './services/api-client'
import { Route, Routes, useNavigate } from 'react-router-dom'
import Home from './pages/Home'

import PastProducts from './pages/Products'
import { Loader } from './components/Loader'
import SalesAgreements from './pages/SalesAgreements'
import SalesAgreement from './features/sales-agreements/SalesAgreement'
import { useScrollToTop } from './util/useScrollToTop'
import Cart from './pages/Cart'
import { useSelector } from 'react-redux'
import { selectCanUseQuickOrder } from './features/azure-b2c/storage'
import { CheckoutSuccess } from './pages/CheckoutSuccess'
import Orders from './pages/Orders'
import OrderDetails from './features/orders/OrderDetails'
import { RequestAQuoteSuccess } from './pages/RequestAQuoteSuccess'
import { DrawdownSuccess } from './pages/DrawdownSuccess'
import ProductDetails from './features/products/pages/ProductDetails'
import { DeleteAccountSuccess } from './pages/DeleteAccountSuccess'
import { MyAccount } from './pages/MyAccount'
import { EnquiryRequestSent } from './pages/EnquiryRequestSent'
import { ProductNotFound } from './features/products/pages/ProductNotFound'
import { SeedOrder } from './pages/SeedOrder'
import { SeedOrderSummary } from './features/seed-orders/SeedOrderSummary'
import { SeedOrderSuccess } from './pages/SeedOrderSuccess'
import { selectRemoteConfig } from './services/firebase/remote-config/remoteConfig.slice'
import QuoteDetails from './features/quotes/QuoteDetails'
import Quotes from './pages/Quotes'
import { QuoteOrderSubmitted } from './pages/QuoteOrderSubmitted'
import * as Sentry from '@sentry/browser'
import { getStores } from './features/stores/storage/stores.slice'
import AppProviders from './AppProviders'
import localStorage from 'redux-persist/es/storage'
import NoAccess from './pages/NoAccess'
import Login from './pages/Login'
import { CampaignSuccess } from './pages/CampaignSuccess'

const Logout = () => {
  const { handleMsalLogout } = useMsalFunctions()

  useEffect(() => {
    handleMsalLogout()
  }, [])
  return <Loader title='Logout' />
}

const Redirect: React.FC<{ path: string }> = ({ path }: { path: string }) => {
  const navigate = useNavigate()

  useEffect(() => {
    navigate(path)
  }, [])

  return <div />
}

const AppRoutes = () => {
  const {
    instance,
    inProgress,
    accounts,
    isShareholder,
    isAuthenticated,
    resetMsalInstance,
    handleMsalLogout,
    createTokenRequest,
  } = useMsalFunctions()

  const navigate = useNavigate()
  const navigationClient = useCallback(() => instantiateCombinedClient(navigate), [navigate])
  const dispatch = useAppDispatch()
  const [initialized, setInitialized] = useState(false)
  const [tokenFetched, setTokenFetched] = useState(false)

  const acquiringTokenCallback = useCallback(async () => {
    // dispatch(setIsLoading(true));
    try {
      if (accounts.length > 0 && inProgress === InteractionStatus.None) {
        try {
          const accessTokenResponse = await instance.acquireTokenSilent(createTokenRequest('login'))
          setTokenFetched(true)
          return accessTokenResponse.accessToken
        } catch (error: any) {
          console.error('Error acquiring token silently:', error)
          if (error instanceof InteractionRequiredAuthError) {
            setTokenFetched(false)
            await instance.acquireTokenRedirect(createTokenRequest('login'))
          } else if (error.errorCode === 'user_cancelled') {
            resetMsalInstance()
          } else if (error.errorCode === 'monitor_window_timeout') {
            await handleMsalLogout()
          }
        }
      }
    } finally {
      //   dispatch(setIsLoading(false));
    }
    return null
  }, [
    accounts,
    inProgress,
    instance,
    tokenFetched,
    resetMsalInstance,
    handleMsalLogout,
    createTokenRequest,
  ])

  const isTokenFetchedAndInitialized = useMemo(() => {
    return tokenFetched && initialized
  }, [initialized, tokenFetched])

  useEffect(() => {
    ;(async () => {
      if (accounts.length > 0) {
        dispatch(AuthSlice.actions.setAuthAccount(accounts[0] as any as AuthAccount))
        if (accounts[0].idToken && !localStorage.getItem('loginTime')) {
          localStorage.setItem('loginTime', Date.now().toString())
        }

        let email = accounts[0].username
        if (accounts[0].idTokenClaims?.emails) {
          // Not actually an array, just poor typing from microsoft
          email = accounts[0].idTokenClaims.emails as unknown as string
        }

        Sentry.setUser({
          email: email,
          username: email,
        })

        const token = await acquiringTokenCallback()
        if (token) {
          apiClient.setDefaultToken(token)
          apiClient.setGetAccessToken(acquiringTokenCallback)
          setInitialized(true)
        }
      }
    })()
  }, [accounts, acquiringTokenCallback, dispatch])

  useEffect(() => {
    instance.setNavigationClient(navigationClient())
  }, [])

  useEffect(() => {
    if (
      // Accounts are msal accounts, not farmlands 'accounts'
      accounts.length > 0 &&
      isTokenFetchedAndInitialized
    ) {
      dispatch(getAccounts())
      dispatch(getStores())
    }
  }, [isTokenFetchedAndInitialized, accounts])

  const farmerAccounts = useSelector(selectAllFarmerAccounts)

  const canUseCart = useSelector(selectCanUseQuickOrder)
  const ref = useRef<HTMLElement>(null)
  const remoteConfig = useSelector(selectRemoteConfig)
  useScrollToTop({ ref })

  const { loadingAccounts, noAccess, ready } = React.useMemo(() => {
    const length = farmerAccounts ? Object.keys(farmerAccounts.accounts).length : 0
    return {
      loadingAccounts: length <= 0,
      noAccess: !isShareholder,
      ready: length > 0,
    }
  }, [farmerAccounts, farmerAccounts?.accounts])
  const getRoutes = () => {
    if (loadingAccounts && !ready) {
      return <Loader title='Loading your account' />
    } else if (!loadingAccounts && noAccess) {
      return (
        <Routes>
          <Route path='/*' element={<NoAccess />} />
        </Routes>
      )
    } else if (isShareholder && isAuthenticated && ready) {
      return (
        <Routes>
          <Route path='/'>
            <Route path='/logout' element={<Logout />} />
            <Route path='/home/*' element={<Home />} />
            <Route path='/products' element={<PastProducts />} />
            <Route path='/products/not-found' element={<ProductNotFound />} />
            <Route path='/products/:masterProductId' element={<ProductDetails />} />
            <Route path='/enquiry-request-sent' element={<EnquiryRequestSent />} />
            {canUseCart && <Route path='/products/cart' element={<Cart />} />}
            {canUseCart && (
              <Route path='/products/checkout/:confirmationId' element={<CheckoutSuccess />} />
            )}
            <Route
              path='/seed'
              element={remoteConfig.seedEnabled ? <SeedOrder /> : <Redirect path='/home' />}
            />
            {remoteConfig.seedEnabled && (
              <>
                <Route
                  path='/seed/summary'
                  element={canUseCart ? <SeedOrderSummary /> : <Redirect path='/home' />}
                />
                <Route
                  path='/seed/success'
                  element={canUseCart ? <SeedOrderSuccess /> : <Redirect path='/home' />}
                />
              </>
            )}

            <Route
              path='/request-quote-success'
              element={
                remoteConfig.quoteEnabled ? <RequestAQuoteSuccess /> : <Redirect path='/home' />
              }
            />
            <Route
              path='/quotes'
              element={remoteConfig.quoteEnabled ? <Quotes /> : <Redirect path='/home' />}
            />
            <Route
              path='/quotes/:quoteId'
              element={remoteConfig.quoteEnabled ? <QuoteDetails /> : <Redirect path='/home' />}
            />
            <Route
              path='/quote-order-submitted/:confirmationId'
              element={
                remoteConfig.quoteEnabled ? <QuoteOrderSubmitted /> : <Redirect path='/home' />
              }
            />

            <Route path='/agreements' element={<SalesAgreements />} />
            <Route path='/agreement/:salesAgreementId' element={<SalesAgreement />} />
            <Route path='/agreement/:salesAgreementId/drawdown' element={<DrawdownSuccess />} />
            <Route path='/agreements/campaign-success' element={<CampaignSuccess />} />

            <Route path='/orders' element={<Orders />} />
            <Route path='/order/:orderId' element={<OrderDetails />} />
            <Route path='/account' element={<MyAccount />} />
            <Route path='/account/delete' element={<DeleteAccountSuccess />} />
            <Route path='/' element={<Redirect path='/home' />} />
            <Route path='/login' element={<Redirect path='/home' />} />
          </Route>
          <Route path='' element={<Redirect path='/home' />} />
        </Routes>
      )
    }
  }
  return (
    <>
      <AuthenticatedTemplate>
        <AppProviders ready={isTokenFetchedAndInitialized} fallback={<div />}>
          {getRoutes()}
        </AppProviders>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Routes>
          <Route path='/logout' element={<Logout key='Logout-Screen' />} />
          <Route path='/*' element={<Login key='Login-Screen' />} />
        </Routes>
      </UnauthenticatedTemplate>
    </>
  )
}

export default AppRoutes
